import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from 'react-router';
import classNames from "classnames";
import { intersection, isArray } from "underscore";

import { agendaCalendarViewEnum, EditorSizes, TemplateClassNames } from "../../../../types/template-layouts";
import {
	PageModule,
	Session,
	SessionPreview,
	SessionTypesEnum,
	SessionTypesFeatures,
	TranslateString,
	Dictionary
} from "../../../../types/working-model";
import { useScreenMediaQuery } from "../../../../utils/use-screen-media-query";
import ModalComponent from "../../../general-ui/modal/modal";
import Search from "../../../general-ui/search-bar/search";
import { SelectOption } from "../../../general-ui/select/select";
import FilterDropdown, { GroupFilterDropdown, IGroupOption, IGroupValue } from './session-filter-dropdown';
import { useTranslate } from "../../../../i18n/useTranslationModules";
import useTranslationRoute from "../../hooks/use-translation-route";
import { getLanguageDropdownDefault } from '../../../../utils/utils';
import { ParamsProps } from '../../live-event';
import Icon, { ICONS } from "../../../general-ui/icon";
import IconToggle from "../../../general-ui/icon-toggle/icon-toggle";
import { OptionalComponent } from "../../../../utils/optional-component";
import { useTypedSelector } from "../../../../store/reducers/use-typed-selector";
import { useModal } from "react-modal-hook";
import { AnimatePresence, domAnimation, LazyMotion, m } from "framer-motion";
import TranslateLangs from "../../../../utils/translate-langs.json";
import '../../../../scss/live-event/base/modules/session-filter.scss';
import { noTrackName, fillOutSessionDisplayOptions } from './agenda-utils';
import { formatMonthAndDay } from "../../../../utils/date-utils";

export interface SessionFilterArgs {
	language?: string;
	search?: string;
	category?: string;
	day?: number;
	/** currently only used for Calendar view filter */
	sessionTypes?: IGroupValue;
	/** currently only used for Calendar view filter */
	viewType?: agendaCalendarViewEnum;
	/** currently only used for Calendar view filter */
	userSavedSessions?: string[];
}
interface SessionFilterProps {
	module?: PageModule;
	isAgendaPage?: boolean;
	template: string | undefined;
	sessions: Array<Session | SessionPreview>;
	isLive: boolean;
	onFilterChange?: (props: SessionFilterArgs) => void;
	overrides: string;
	userSavedSessions?: string[];
	calendarViewFilters?: boolean;
}
export const SessionFilter = ({
	sessions,
	onFilterChange,
	template,
	overrides,
	userSavedSessions,
	calendarViewFilters = false,
	module,
	isAgendaPage = false,
}: SessionFilterProps): JSX.Element => {
	const automaticSessionFiltering = useTypedSelector(state => state.LiveEventReducer.eventBundle?.automatic_session_filter_by_user_language);
	const eventId = useTypedSelector(state => state.LiveEventReducer.eventBundle?.event);
	const registrationLang = useTypedSelector(state => state.LiveEventReducer.registeredLanguage);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer?.workingEvent);
	const eventBundle = useTypedSelector(state => state.LiveEventReducer?.eventBundle);
	const editorSize = useTypedSelector(state => state.CreateEventReducer?.editorSize);
	const translationTick = useTypedSelector(state => state.LiveEventReducer.translationUpdates);

	const { language } = useParams() as ParamsProps;
	const translationRoute = useTranslationRoute();
	const { t } = useTranslate(["homepage", translationRoute]);

	const [selectedLanguage, setSelectedLanguage] = useState<string>(
		automaticSessionFiltering ? getLanguageDropdownDefault(eventId, registrationLang, language, t) : ''
	);
	const [selectedDate, setSelectedDate] = useState<number>(0);
	const [selectedCategory, setSelectedCategory] = useState<string>("");
	const [selectedSessionType, setSelectedSessionType] = useState<IGroupValue>();
	const [selectedViewType, setSelectedViewType] = useState<agendaCalendarViewEnum>(agendaCalendarViewEnum.LIST);
	const [selectedMyList, setSelectedMyList] = useState(false);
	const [displayCalendarMobileFilters, setDisplayCalendarMobileFilters] = useState(false);
	const [, setSearch] = useState<string>();

	const { isLessThan1024, isLessThan768 } = useScreenMediaQuery();
	const isEditorLessThan1024 = [EditorSizes.tablet].includes(editorSize as EditorSizes);
	const isEditorLessThan768 = [EditorSizes.mobile].includes(editorSize as EditorSizes);
	const isEditorDesktop = [EditorSizes.desktop].includes(editorSize as EditorSizes);
	const event = workingEvent ?? eventBundle;

	const filterableSessions = useMemo(() => {
		// onDemand sessions are not shown in calendar view so they shouldn't be considered when setting filters
		if (selectedViewType === agendaCalendarViewEnum.CALENDAR) {
			return sessions.filter(session => session.session_type !== SessionTypesEnum.onDemand);
		}

		// Only show selected sessions
		// If no selections passed in, skip
		// note: Agenda displays all tracks all the time
		if (!module || !module.module_display_info || isAgendaPage) return sessions;

		// Filtered to sessions that are selected for display
		const sessionDisplayAll = module.module_display_info?.session_display_all || { liveSessions: true, onDemandSessions: true };
		const sessionDisplayOptions = module?.module_display_info?.session_display_options || {};
		const normalizedSessionDisplayOptions = fillOutSessionDisplayOptions(sessionDisplayOptions, sessionDisplayAll, sessions, false);
		const selectedSessionIds = Object.values(normalizedSessionDisplayOptions).flat();
		const filteredSessions = sessions.filter(({ session: sessionId }) => selectedSessionIds.includes(sessionId));
		return filteredSessions;
	}, [selectedViewType, sessions, module, isAgendaPage]);
	const moreThan1FilterableSession = filterableSessions.length > 1;

	const daysOptions = useMemo<SelectOption[]>(() => {
		const existingDays: (string | number)[] = [];
		const result = filterableSessions
			.map((s) => {
				const date = new Date(s.timestamp || "");
				if (isNaN(date.getTime())) {
					return null;
				}

				const label = formatMonthAndDay({ date, locale: language });

				return {
					label,
					value: s.timestamp || 0,
				} as SelectOption;
			})
			.filter((f) => {
				if (!f) return false;
				// don't include duplicate days
				// use labels as timestamps are different for same day events with differing times
				const alreadyExists = existingDays.includes(f.label);
				existingDays.push(f.label);
				return !alreadyExists;
			}) as SelectOption[];
		return result;
		// include translation tick to force re-render when translation json is loaded (otherwise no content will show)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [language, filterableSessions, t, translationTick]);

	const inlineDaysOptions = useMemo<SelectOption[]>(() => {
		return [{ label: t("All"), value: 0 }, ...daysOptions];
	}, [daysOptions, t]);

	const selectDaysOptions = useMemo<SelectOption[]>(() => {
		return [{ label: t("All dates"), value: 0 }, ...daysOptions];
	}, [daysOptions, t]);

	const languageOptions = useMemo<SelectOption[]>(() => {
		const result = filterableSessions
			.map((s) => s.languages)
			.flat()
			.filter((x, i, a) => a.indexOf(x) === i)
			.map((s) => {
				const fullLanguage = TranslateLangs.find(f => f.code === s);
				return {
					// t function and typescript does not play well with variables, therefore must use "any"
					label: t(`Languages.${fullLanguage?.code}` as any) || fullLanguage?.label || '',
					value: s,
				};
			});
		return [{ label: t("All languages"), value: "" }, ...result];
		// include translation tick to force re-render when translation json is loaded (otherwise no content will show)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterableSessions, t, translationTick]);

	const categoryOptions = useMemo<SelectOption[]>(() => {
		const result = filterableSessions
			.map((s) => s.tracks)
			.flat()
			.filter((x, i, a) => a.indexOf(x) === i)
			.map(
				(s) => {
					return ({
						label: t(`${translationRoute}:tracks.${s}`, s ?? ''),
						value: s,
					} as SelectOption);
				}
			);
		result.sort((a: SelectOption, b: SelectOption) => a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1);
		// TODO only push this on if untracked is set to show any.
		result.push({ label: 'Uncategorized', value: noTrackName });
		return [
			...(!calendarViewFilters ? [{ label: t("All tracks"), value: "" }] : []),
			...result
		];
		// include translation tick to force re-render when translation json is loaded (otherwise no content will show)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [calendarViewFilters, filterableSessions, t, translationRoute, translationTick]);

	const sessionTypeOptions = useMemo<SelectOption[]>(() => {
		const labelMap = filterableSessions.reduce((a: { [label: string]: Session["session_type"]; }, c) => {
			a[SessionTypesFeatures[c.session_type].display_name] = c.session_type;
			return a;
		}, {});
		const result = Object.entries(labelMap).map(([label, value]) => ({ label, value }));

		return result;
	}, [filterableSessions]);

	const tagOptions = useMemo<SelectOption[]>(() => {
		const tagsInUse = filterableSessions.map(s => s.tags).flat();
		const tagsInUseMap = tagsInUse.reduce((a: Dictionary, c) => {
			if (c) a[c] = true;
			return a;
		}, {});
		const result = event?.tags
			?.filter(t => tagsInUseMap[t.uuid]) // remove any unused tags - probably another way without nested loop
			?.map(t => ({ label: t.name, value: t.uuid }))
			?? [];

		return result;
		// include translation tick to force re-render when translation json is loaded (otherwise no content will show)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [event?.tags, filterableSessions, translationTick]);

	const groupedFilterOptions: IGroupOption[] = useMemo(() => {
		return [
			{ title: 'All sessions', options: [], selectAll: true },
			sessionTypeOptions.length ? { title: 'Sessions', options: sessionTypeOptions } : [],
			tagOptions.length ? { title: 'Tags', options: tagOptions } : [],
			categoryOptions.length ? { title: 'Tracks', options: categoryOptions } : [],
		].flat();
		// include translation tick to force re-render when translation json is loaded (otherwise no content will show)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [categoryOptions, sessionTypeOptions, tagOptions, translationTick]);

	useEffect(() => {
		// initialize selected for category filter
		const selectedValues: IGroupValue = { selectAll: true } as IGroupValue;
		groupedFilterOptions.forEach(category => {
			selectedValues[category.title] = category.options.map(option => option.value);
		});

		setSelectedSessionType(selectedValues);
		// we just want this to run on initial load
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	
	const renderInlineDays = () => {
		const data = inlineDaysOptions.map((day) => {
			const label = day.label;

			return (
				<button
					key={day.value}
					onClick={() => {
						setSelectedDate(day.value as number);
						onFilterChange?.({
							category: selectedCategory,
							day: day.value as number,
							language: selectedLanguage,
							sessionTypes: selectedSessionType,
							viewType: selectedViewType,
							userSavedSessions: selectedMyList ? userSavedSessions : undefined
						});
					}}
					className={classNames('evt-button secondary session-day-button', {
						"session-selected-day ": day.value === selectedDate,
					})}
				>
					{label}
				</button>
			);
		});
		return (
			<div className="agenda-inline-days">
				{data}
			</div>
		);
	};

	const renderDaysFilter = (): JSX.Element => {
		return (
			<>
				{/* Test to any value for test inline view */}
				{/* Tablet view to always render dropdown */}
				{daysOptions.length < 4 && !isLessThan1024 && isEditorDesktop ? (
					renderInlineDays()
				) : (
					<FilterDropdown
						options={selectDaysOptions}
						onChange={(option) => {
							const date = parseInt(option.value.toString(), 10);

							setSelectedDate(date || 0);
							onFilterChange?.({
								category: selectedCategory,
								day: date,
								language: selectedLanguage,
								sessionTypes: selectedSessionType,
								viewType: selectedViewType,
								userSavedSessions: selectedMyList ? userSavedSessions : undefined
							});
						}}
						template={template || TemplateClassNames.Apollo}
						value={selectedDate}
						initialTitle={t('homepage:All dates')}
						className="days-filter"
						animateDropdown={calendarViewFilters}
					/>
				)}
			</>
		);
	};

	const today = new Date().setHours(0, 0, 0, 0);
	const sessionsAreScheduledToday = daysOptions.find(day => new Date(day.value).setHours(0, 0, 0, 0) === today);
	const renderTodayButton = () => {
		if (!sessionsAreScheduledToday) return null;
		let date = parseInt(sessionsAreScheduledToday.value.toString(), 10);
		const todayIsSelected = selectedDate === date;

		const handleClick = () => {
			if (todayIsSelected) {
				date = 0;
			}
			setSelectedDate(date);
			onFilterChange?.({
				category: selectedCategory,
				day: date,
				language: selectedLanguage,
				sessionTypes: selectedSessionType,
				viewType: selectedViewType,
				userSavedSessions: selectedMyList ? userSavedSessions : undefined
			});
		};

		return (
			<button
				className={classNames("evt-button today-btn", { primary: todayIsSelected, secondary: !todayIsSelected })}
				onClick={handleClick}>
				{t("Today")}
			</button>
		);
	};
	
	const currentDateIndex = daysOptions.findIndex(day => day.value === selectedDate);
	const renderDayArrows = () => {
		const handleClick = (direction: 'prev' | 'next') => {
			let next;
			let prev;
			let date;

			if (!currentDateIndex) {
				// this is the All Days option. next is the first day, prev is the last day
				next = daysOptions[1];
				prev = daysOptions[daysOptions.length - 1];

				date = parseInt((direction === 'next' ? next : prev).value.toString(), 10);
			} else {
				// if this is the last day, then next click should select all days (0)
				next = (currentDateIndex === daysOptions.length - 1) ? 0 : currentDateIndex + 1;
				prev = currentDateIndex - 1;
				date = parseInt(daysOptions[(direction === 'next' ? next : prev)].value.toString(), 10);
			}

			setSelectedDate(date);
			onFilterChange?.({
				category: selectedCategory,
				day: date,
				language: selectedLanguage,
				sessionTypes: selectedSessionType,
				viewType: selectedViewType,
				userSavedSessions: selectedMyList ? userSavedSessions : undefined
			});

		};

		return (
			<div className="change-day-controls">
				<button className="evt-button secondary no-padding change-day prev-day" onClick={() => handleClick('prev')}>
					<Icon name={ICONS.KEYBOARD_ARROW_LEFT} color="" size={16} />
				</button>
				<button className="evt-button secondary no-padding change-day next-day" onClick={() => handleClick('next')}>
					<Icon name={ICONS.KEYBOARD_ARROW_RIGHT} color="" size={16} />
				</button>
			</div>
		);
	};

	const renderLanguageFilter = (): JSX.Element => {
		return (
			<FilterDropdown
				options={languageOptions}
				onChange={(option) => {
					const value = option.value.toString();
					setSelectedLanguage(value);

					onFilterChange?.({
						category: selectedCategory,
						day: selectedDate,
						language: value,
						sessionTypes: selectedSessionType,
						viewType: selectedViewType,
						userSavedSessions: selectedMyList ? userSavedSessions : undefined
					});
				}}
				template={template || TemplateClassNames.Apollo}
				value={selectedLanguage}
				initialTitle={automaticSessionFiltering ? t(`Languages.${getLanguageDropdownDefault(eventId, registrationLang, language, t)}`) : t('All languages')}
				isLangDropdown
				className="language-filter"
				animateDropdown={calendarViewFilters}
			/>
		);
	};

	const renderCategoryFilter = (): JSX.Element => {
		return (
			<FilterDropdown
				options={categoryOptions}
				onChange={(option) => {
					const value = option.value.toString();

					setSelectedCategory(value);

					onFilterChange?.({
						category: value,
						day: selectedDate,
						language: selectedLanguage,
						sessionTypes: selectedSessionType,
						viewType: selectedViewType,
						userSavedSessions: selectedMyList ? userSavedSessions : undefined
					});
				}}
				template={template || TemplateClassNames.Apollo}
				value={selectedCategory}
				initialTitle={t("All tracks")}
				className="category-filter"
				animateDropdown={calendarViewFilters}
			/>
		);
	};

	const renderGroupFilter = (): JSX.Element => {
		if (!selectedSessionType) return <></>;
		return (
			<GroupFilterDropdown
				groupOptions={groupedFilterOptions}
				onChange={value => {
					setSelectedSessionType(value);
					onFilterChange?.({
						category: selectedCategory,
						day: selectedDate,
						language: selectedLanguage,
						sessionTypes: value,
						viewType: selectedViewType,
						userSavedSessions: selectedMyList ? userSavedSessions : undefined
					});
				}}
				template={''}
				value={selectedSessionType}
				className="session-type-filter"
				animateDropdown={calendarViewFilters}
			/>
		);
	};

	const canDisplayCalendar = sessions.some(s => SessionTypesFeatures[s.session_type].has_start_time);
	const renderViewTypeFilter = () => {
		return (
			<IconToggle
				name="view toggle"
				leftValue={agendaCalendarViewEnum.CALENDAR}
				leftIconName={ICONS.CALENDAR}
				rightValue={agendaCalendarViewEnum.LIST}
				rightIconName={ICONS.LAYOUT_LIST}
				onToggle={e => {
					const value = e.target.value as agendaCalendarViewEnum;
					setSelectedViewType(e.target.value as agendaCalendarViewEnum);

					if (value === agendaCalendarViewEnum.LIST) {
						// remove overflow-x hidden class
						document.querySelector('.registered-main-content')?.classList.remove('hide-x-overflow-tablet', 'hide-x-overflow-mobile');
						document.querySelector('section.agenda-calendar')?.classList.remove('hide-x-overflow-tablet', 'hide-x-overflow-mobile');
					} else {
						// if calendar view, block overflow-x scroll on parent containers (tablet/mobile) so we don't get overflow issues
						document.querySelector('.registered-main-content')?.classList.add('hide-x-overflow-tablet', 'hide-x-overflow-mobile');
						document.querySelector('section.agenda-calendar')?.classList.add('hide-x-overflow-tablet', 'hide-x-overflow-mobile');
					}

					onFilterChange?.({
						category: selectedCategory,
						language: selectedLanguage,
						day: selectedDate,
						sessionTypes: selectedSessionType,
						viewType: value,
						userSavedSessions: selectedMyList ? userSavedSessions : undefined
					});
				}}
				selected={selectedViewType}
			/>
		);
	};

	const handleSearchChange = useCallback(
		(text) => {
			setSearch(text);
			onFilterChange?.({
				category: selectedCategory,
				search: text,
				language: selectedLanguage,
				day: selectedDate,
				sessionTypes: selectedSessionType,
				viewType: selectedViewType,
				userSavedSessions: selectedMyList ? userSavedSessions : undefined
			});
		},
		[onFilterChange, selectedCategory, selectedDate, selectedLanguage, selectedMyList, selectedSessionType, selectedViewType, userSavedSessions]
	);

	const renderSearch = (): JSX.Element => (
		<>
			{moreThan1FilterableSession && (
				<Search
					searchOnEmpty
					onSearch={handleSearchChange}
					onClear={() => handleSearchChange("")}
				/>
			)}
		</>
	);

	const renderContent = (): JSX.Element => {
		return (
			<>
				{daysOptions.length > 2 ? renderDaysFilter() : null}
				{languageOptions.length > 2 ? renderLanguageFilter() : null}
				{categoryOptions.length > 2 ? renderCategoryFilter() : null}
			</>
		);
	};

	if (calendarViewFilters) {
		if (sessions.length === 1) return <></>;
		// calendar filters mobile
		const shouldDisplayGroupFilter = groupedFilterOptions.length > 1 || groupedFilterOptions[0]?.options?.length > 1;
		if (isLessThan1024 || isEditorLessThan1024 || isEditorLessThan768) {
			return (
				<div
					className={classNames(
						"agenda-filters",
						overrides,
					)}
				>
					<div className="filter-row">
						<div className="filter-col left">
							{sessionsAreScheduledToday && moreThan1FilterableSession && ((!isLessThan768 && isEditorDesktop) || isEditorLessThan1024) ? renderTodayButton() : null}
							{daysOptions.length > 2 && moreThan1FilterableSession ? renderDaysFilter() : null}
							{daysOptions.length > 2 && moreThan1FilterableSession && ((!isLessThan768 && isEditorDesktop) || isEditorLessThan1024) ? renderDayArrows() : null}
						</div>

						<div className="filter-col right">
							<OptionalComponent display={moreThan1FilterableSession}>
								<button
									onClick={() => setDisplayCalendarMobileFilters(prev => !prev)}
									className={classNames("agenda-filters-button evt-button secondary no-padding", { on: displayCalendarMobileFilters })}
								>
									<Icon
										name={ICONS.FILTER}
										color=""
										size={20}
									/>
								</button>
							</OptionalComponent>
							{canDisplayCalendar && renderViewTypeFilter()}
						</div>
					</div>
					<OptionalComponent display={displayCalendarMobileFilters}>
						<div className="mobile-filters-wrapper">
							<AnimatePresence>
								<LazyMotion features={domAnimation}>
									<m.div
										initial={{ translateY: -100, opacity: 0 }}
										animate={{ translateY: 0, opacity: 1 }}
										exit={{ translateY: -100, opacity: 0 }}
										style={{ width: '100%' }}
									>
										<div className="filter-row">
											{renderSearch()}
											{shouldDisplayGroupFilter && moreThan1FilterableSession ? renderGroupFilter() : null}
											{languageOptions.length > 2 && moreThan1FilterableSession ? renderLanguageFilter() : null}
										</div>
									</m.div>
								</LazyMotion>
							</AnimatePresence>
						</div>
					</OptionalComponent>
				</div>
			);
		}

		// calendar filters tablet
		if (isLessThan1024 || isEditorLessThan1024 || isEditorLessThan768) {
			return (
				<div
					className={classNames(
						"agenda-filters",
						overrides,
					)}
				>
					<div className="filter-row">
						{sessionsAreScheduledToday && moreThan1FilterableSession ? renderTodayButton() : null}
						{daysOptions.length > 2 && moreThan1FilterableSession ? renderDaysFilter() : null}
						{daysOptions.length > 2 && moreThan1FilterableSession ? renderDayArrows() : null}
						{renderSearch()}
					</div>
					<div className="filter-row">
						<div className="filter-col">
							{shouldDisplayGroupFilter && moreThan1FilterableSession ? renderGroupFilter() : null}
							{languageOptions.length > 2 && moreThan1FilterableSession ? renderLanguageFilter() : null}
						</div>
						<div className="filter-col right">
							{canDisplayCalendar && renderViewTypeFilter()}
						</div>
					</div>
				</div>
			);
		}

		// calendar filters desktop
		return (
			<div
				className={classNames(
					"agenda-filters",
					overrides,
				)}
			>
				{sessionsAreScheduledToday && moreThan1FilterableSession && daysOptions.length > 4 ? renderTodayButton() : null}
				{daysOptions.length > 2 && moreThan1FilterableSession ? renderDaysFilter() : null}
				{daysOptions.length > 2 && moreThan1FilterableSession && daysOptions.length > 4 ? renderDayArrows() : null}
				{renderSearch()}
				{shouldDisplayGroupFilter && moreThan1FilterableSession ? renderGroupFilter() : null}
				{languageOptions.length > 2 && moreThan1FilterableSession ? renderLanguageFilter() : null}
				{canDisplayCalendar && renderViewTypeFilter()}
			</div>
		);
	}


	const [hideFilterModal] = useModal(() => {
		return (
			<ModalComponent
				className={classNames("evt-modal-filters live-event-container", template)}
				title={``}
				onRequestClose={hideFilterModal}
				open={true}
				closeable
				cancellable
			>
				{renderContent()}
			</ModalComponent>
		);
	}, [selectedCategory, selectedDate, selectedLanguage, template]);

	return sessions.length > 1 ? (
		<>
			<div
				className={classNames(
					"agenda-filters",
					overrides,
				)}
			>
				<OptionalComponent display={!calendarViewFilters}>
					{renderSearch()}
				</OptionalComponent>
				{renderContent()}
			</div>
		</>
	) : <></>;
};

interface FilterableSession {
	title: TranslateString;
	tracks: string[] | null | undefined;
	languages: string[];
	timestamp: number | null | undefined;
	session_type: SessionTypesEnum;
	uuid: string;
	tags?: string[];
}

export function filterSessions<T extends FilterableSession>({
	sessions,
	filter,
}: {
	sessions: T[];
	filter?: SessionFilterArgs;
}): T[] {
	let result = [...sessions];
	if (filter?.search) {
		result = result.filter(s => s.title.base.toUpperCase().includes((filter.search || '').toUpperCase()));
	}
	if (filter?.category) {
		result = result.filter((f) => f.tracks?.includes(filter.category || ""));
	}
	if (filter?.language) {
		result = result.filter((f) => f.languages.includes(filter.language || ""));
	}
	if (filter?.day) {
		//turn timestamps into days, to get rid of the exact time factor in timestamps
		const getDay = (date: Date) => date.toLocaleString("default", { month: "long" }) + date.getDate();
		result = result.filter((f) => {
			if (!f.timestamp || !filter.day) return false;
			const currentDay = getDay(new Date(f.timestamp));
			const filterDay = getDay(new Date(filter.day));
			return currentDay === filterDay;
		});
	}
	if (filter?.sessionTypes) {
		// go through each group and filter based on if a session has that attribute
		result = result.filter(s => {
			if (filter?.sessionTypes?.selectAll) return true;
			if (filter?.sessionTypes?.Sessions?.length && filter?.sessionTypes?.Sessions.includes(s.session_type)) {
				return true;
			}
			if (filter?.sessionTypes?.Tags?.length && !!intersection(filter?.sessionTypes?.Tags, s?.tags ?? []).length) {
				return true;
			}

			if (filter?.sessionTypes?.Tracks && !!intersection(filter?.sessionTypes?.Tracks, s?.tracks ?? []).length) {
				return true;
			}
			// this is uncategorized tracks for on-demand and if in filter, need to return true to show
			if (
				s.session_type === SessionTypesEnum.onDemand
				&& filter?.sessionTypes?.Tracks.includes(noTrackName)
				&& isArray(s?.tracks)
				&& s?.tracks?.length === 0
			) {

				return true;
			}
			return false;
		});
		// if a session has any of the attributes inside of the session filterTYpes, then we should include it in the array
	}
	if (filter?.userSavedSessions?.length) {
		result = result.filter(session => {
			if (filter.userSavedSessions?.includes(session.uuid)) {
				return session;
			}
		});
	}
	return result;
}

interface useSessionListSizeProps {
	step: number;
	size: number;
	setSize: (size: number) => void
}
export function useSessionListSize(/*content: PageModule["content"]*/): useSessionListSizeProps {
	//Currently the number of session shown at once is 8, leaving this code here in case we want to make it dynamic/layout dependent again.

	// const layout_type = content?.layout_type || AgendaLayoutTypesClassic.standard;
	// const isFourSessions = useMemo(
	// 	() =>
	// 		layout_type === AgendaLayoutTypesClassic.contained_card ||
	// 		layout_type === AgendaLayoutTypesClassic.contained_card_no_image ||
	// 		layout_type === AgendaLayoutTypesBold.contained_card ||
	// 		layout_type === AgendaLayoutTypesFun.contained_card,
	// 	[layout_type]
	// );

	// const step = useMemo(() => (isFourSessions ? 2 : 3), [isFourSessions]);
	// const [size, setSize] = useState(isFourSessions ? 4 : 3);
	const step = 2;
	const [size, setSize] = useState(24);
	return {
		step,
		size,
		setSize
	};
}
